import { PurchaseOrder } from '~lmo/models/purchaseOrders.model';
import { Account } from './account.model';
import { BillingAttachment } from './billingAttachment.model';
import { DistributionCenter } from './distributionCenter.model';
import { Mesh } from './mesh.model';
import { Mine } from './mine.model';
import { Truck } from './truck.model';
import { User } from './user.model';

export type OrderStatus =
  | 'pending'
  | 'driver_rejected'
  | 'declined'
  | 'dispatched'
  | 'driver_accepted'
  | 'canceled'
  | 'completed';

export interface BoxPickup {
  id: number;
  siteName: string;
  siteRadius: number;
  ownerName: string;
  lngLat: [number, number];
  entranceLngLat?: [number, number];
  completed: boolean;
  beforeOrder: boolean;
}

export interface OneClickApproval {
  isEligible: boolean;
  isManuallyPreApproved: boolean;
  manuallyApprovedByName: string | null;
  failureReason: string | null;
}

export interface Order {
  id: number;
  truck: Truck;
  mine: Mine;
  distributionCenter: DistributionCenter;
  fracId: number;
  mesh: Mesh;
  user: User;
  vendor: Account;
  vendorContractId: number | null;
  loaded: boolean;
  eta: number;
  etaStatus: 'none' | 'arrived' | 'unloading' | 'loading' | 'staging';
  endTimestamp: string;
  loadWeight: number;
  loadNumber: number;
  orderStatus: OrderStatus;
  bolNumber: string;
  boxes: Box[];
  poNumber: string;
  isUpForGrabs: boolean;
  createTimestamp: string;
  startTimestamp: string;
  dispatchTimestamp: string;
  acceptTimestamp: string;
  updateTimestamp: string;
  estimateDeliveryTimestamp: string;
  waitAtLoader: number;
  waitAtWell: number;
  hourlyDetentionRate: number;
  pickupFreeTime: number;
  loadingDetention: number;
  dropOffFreeTime: number;
  unloadingDetention: number;
  deadheadFreeMileage: number;
  deadheadCostPerMile: number;
  deadheadCost: number;
  lineHaul: number;
  billingStatus: 'approved_by_lmo' | 'approved' | 'approved_by_dispatcher' | 'not_billed' | 'billed';
  additionCharge: number;
  additionDeduction: number;
  comment: string;
  dataUploadTimestamp: string | null;
  dataMatched: string | null;
  orderStatusChangeHistory: OrderStatusChangeHistory[];
  lmoLoaderEmail: LmoLoaderEmail;
  mineSiteVisit: MineSiteVisit;
  fracSiteVisit: FracSiteVisit;
  trailer?: {
    name: string;
  };
  reroutedFrom?: Reroute;
  reroutedTo?: Reroute;
  ticketNumber: string;
  description: string;
  actualLoadWeight: number;
  bulkStorageNumber: string;
  saUniqueId: string;
  jobName: string;
  urgent: boolean;
  boostPercent: number;
  uploadedFiles: BillingAttachment[];
  driverRating: number;
  canRateDriver: boolean;
  cannotCancel: boolean;
  isPreLoad: boolean;
  purchaseOrder: PurchaseOrder;
  boxPickup?: BoxPickup;
  pendingBoxPickup?: boolean;
  autoDispatchPaused?: boolean;
  autoDispatchPauseEndsAt?: string;
  doNotAutoDispatch?: boolean;
  onBoxMove?: boolean;
  loaderDataLocked?: boolean;
  dataLockDetails?: DataLockDetails;
  classicSubcontractorName?: string;
  verificationAction?: string;
  verificationTime?: string;
  verifiedBy?: string;
  trailerType?: {
    trailerTypeId: number;
    trailerTypeName: string;
    trailerTypeDisplayName: string;
  };
  replacedByOrderId?: number;
  subcarrierName?: string;
  boxPickupOORMiles?: BoxPickupOORMiles;
}

export interface DataLockDetails {
  isLocked: boolean;
  lastActionUserID?: number;
  lastActionUserName?: string;
  lastActionTimestamp?: string;
  canLock: boolean;
  canUnlock: boolean;
}

interface Reroute {
  uid: number;
  orderId: number;
  fracId: number;
  fracName: string;
  mineId: number;
  mineName: string;
  loadNumber: number;
  orderStatus: OrderStatus;
}

export interface OrderStatusChangeHistory {
  orderId: number;
  changedBy: string;
  changedTimestamp: string;
}

export interface Box {
  id: number;
  name: string;
  orderId: number;
  loadWeight: number;
  actualLoadWeight?: number;
}

export interface LmoLoaderEmail {
  id: number;
  emailSettingsId: number;
  sender: string;
  uploadTime: string | null;
  uploadCompleted: boolean;
  csvConverted: boolean;
  processed: boolean;
  truckTimeOrdersMatched: string | null;
  lmoLoaderEmailSettings: string | null;
  files: string[] | null;
}

export interface MineSiteVisit {
  id: number;
  orderId: number;
  siteId: number;
  startTimestamp: string;
  endTimestamp: string;
}

export interface FracSiteVisit extends MineSiteVisit {
  id: number;
  orderId: number;
  siteId: number;
  startTimestamp: string;
  endTimestamp: string;
  wellEntryTimestamp?: string;
}

export interface OrderSummary {
  bolNumber: string;
  boxIds?: any;
  boxNames: string[];
  destinationEta: string;
  destinationType: 'N/A';
  driverId: number;
  driverName?: string;
  id: number;
  isLocked: boolean;
  loadDetailsIncomplete: boolean;
  loadNumber: number;
  lockReason: string;
  meshId: number;
  meshName: string;
  mineId: number;
  mineName: string;
  missingLoadDetails: string[];
  reroutedFromSiteName: string;
  reroutedToSiteName: string;
  status: OrderStatus;
  ticketNumber: string;
  time: string;
  trailerId?: number;
  trailerName: string;
  truckName: string;
  truckingVendorId: number;
  truckingVendorName: string;
  saUniqueId: string;
  verificationAction?: string;
  verificationTime?: string;
  verifiedBy?: string;
}

export interface OrderDetailsUpdate {
  actualLoadWeight: number;
  boxes: Box[];
  bulkStorageNumber: string;
  bolNumber: string;
  ticketNumber: string;
  description: string;
  truckId: number;
}

export interface OrderDetailsVerify {
  action: string;
  finalLoadWeight: number;
  boxes: BoxVerify[];
  bolNumber: string;
}

export interface BoxVerify {
  id: number;
  finalLoadWeight: number;
}

export interface OrderCreate {
  fracId: number;
  mine: {
    id: number;
  };
  mesh: {
    id: number;
  };
  purchaseOrder: {
    id: number;
  };
  vendor: {
    id: number;
  };
  startTimestamp: string;
  createTimestamp: string;
  doNotAutoDispatch?: boolean;
  deadheadLinkFrom?: number;
  createBoxPickupInfo?: {
    boxPickupSiteId: number;
    outOfRouteMiles: number;
    outOfRouteRateCents: number;
    firstStop: boolean;
  };
  trailerType?: {
    trailerTypeId: number;
  };
}

export interface PO {
  id: number;
  name: string;
}

export interface OrdersGroupedWithInProgressStatus {
  id: number;
  inProgressStatus: string;
  orders: Order[];
}

export interface MeshWithOrders extends Mesh {
  orders: Order[];
}

export interface DiversionRoutes {
  fracId: number;
  fracName: string;
  purchaseOrders: PO[];
  lastWaypointBefore: number;
  timeToReachWell: number;
  totalStorageAmount: number;
  totalVolumeInStorage: number;
}

export function isPending(order: Order): boolean {
  return order.orderStatus === 'pending';
}

export function isDriverRejected(order: Order): boolean {
  return order.orderStatus === 'driver_rejected';
}

export function isDeclined(order: Order): boolean {
  return order.orderStatus === 'declined';
}

export function isDispatched(order: Order): boolean {
  return order.orderStatus === 'dispatched';
}

export function isDriverAccepted(order: Order): boolean {
  return order.orderStatus === 'driver_accepted';
}

export function isCancelled(order: Order): boolean {
  return order.orderStatus === 'canceled';
}

export function isComplete(order: Order): boolean {
  return order.orderStatus === 'completed';
}

export function isFinished(order: Order): boolean {
  return order.orderStatus === 'completed' || order.orderStatus === 'canceled';
}

export function isPendingGroup(order: Order): boolean {
  return isPending(order) || isDriverRejected(order) || isDeclined(order);
}

export function isInProgressGroup(order: Order): boolean {
  return isDispatched(order) || isDriverAccepted(order);
}

export function isCompleteGroup(order: Order): boolean {
  return isCancelled(order) || isComplete(order);
}

export function isEtaNone(order: Order): boolean {
  return order.etaStatus === 'none';
}

export function isEtaArrived(order: Order): boolean {
  return order.etaStatus === 'arrived';
}

export function isEtaLoading(order: Order): boolean {
  return order.etaStatus === 'loading';
}

export function isEtaUnloading(order: Order): boolean {
  return order.etaStatus === 'unloading';
}

export function isEtaStaging(order: Order): boolean {
  return order.etaStatus === 'staging';
}

export function isBillingStatusApprovedByLmo(order: Order): boolean {
  return order.billingStatus === 'approved_by_lmo';
}

export function isBillingStatusApprovedByDispatcher(order: Order): boolean {
  return order.billingStatus === 'approved_by_dispatcher';
}

export function isBillingStatusApproved(order: Order): boolean {
  return order.billingStatus === 'approved';
}

export function isBillingStatusNotBilled(order: Order): boolean {
  return order.billingStatus === 'not_billed';
}

export function isBillingStatusBilled(order: Order): boolean {
  return order.billingStatus === 'billed';
}

export function inProgressStatusIndexKey(): Record<string, number> {
  return {
    'Sent to Driver': 0,
    'To Loader': 1,
    'To Well': 2,
  };
}

export function isLmoEditable(order: Order): boolean {
  return isBillingStatusApproved(order) || isBillingStatusApprovedByLmo(order) || isBillingStatusNotBilled(order);
}

export interface DeadheadLinkableOrder {
  id: number;
  uniqueId: string;
  mineName: string;
  fracName: string;
  endTimestamp: string;
  driverName: string;
}

export interface BoxPickupOORMiles {
  orderID: number;
  oorMiles: number;
  siteName: string;
}
